/**
 * The chip slot: ONE capsule at the top of the app that changes what it is.
 *
 * Two different components have always wanted that spot — the green
 * "N Agents · N Skills" pill (`AgentsIslandBadge`, header chrome, route-gated)
 * and the minimized call (`VoiceCallIsland`, fixed, outlives navigation). They
 * live in different parts of the tree and neither can move: the pill has to be
 * laid out by the header it belongs to, the island has to survive the route
 * change that is the entire point of minimizing.
 *
 * So the slot is modelled here instead of in either component: a tiny state
 * machine that says WHICH chip owns the spot, and whether a morph between two
 * owners is in flight. While one is, neither real chip is drawn — a shared
 * overlay layer owns the capsule and animates it from one rect to the other.
 * That is what makes the swap read as one thing changing shape rather than two
 * widgets trading places, and it is why "exactly one chip is on screen" is an
 * invariant this module can be tested for without a DOM.
 */

export type ChipKind = 'pill' | 'island';

export type ChipMorph = {
  /** Where the capsule starts. Null when it grows in from nothing. */
  from: ChipKind | null;
  /** Where it lands. Null when it shrinks away to nothing. */
  to: ChipKind | null;
};

export type ChipSlotState = {
  /** Who owns the slot once everything settles. */
  occupant: ChipKind | null;
  /** In flight; while set, the overlay draws the capsule and no real chip does. */
  morph: ChipMorph | null;
};

export const EMPTY_SLOT: ChipSlotState = { occupant: null, morph: null };

export type ChipSlotEvent =
  /** The app decided who should own the slot now. */
  | { type: 'occupy'; kind: ChipKind | null; reducedMotion?: boolean }
  /** The overlay finished animating. */
  | { type: 'settle' };

export function reduceChipSlot(state: ChipSlotState, event: ChipSlotEvent): ChipSlotState {
  switch (event.type) {
    case 'occupy': {
      if (event.kind === state.occupant && !state.morph) return state;
      // Reduced motion is not a shorter animation — it is no animation. The
      // occupant changes and the overlay never appears, so there is no
      // half-morphed state for the setting to leave behind.
      if (event.reducedMotion) return { occupant: event.kind, morph: null };
      // Retargeting mid-flight keeps the ORIGINAL start point: the capsule is
      // physically where the last morph left it, not where the abandoned
      // destination was.
      const from = state.morph ? state.morph.from : state.occupant;
      if (from === event.kind) return { occupant: event.kind, morph: null };
      // Only a CALL arriving or leaving is worth morphing. The pill coming and
      // going with the route (settings, Your Agents) is ordinary chrome and has
      // always just been there or not — animating that would put a new bit of
      // motion on every navigation nobody asked for. A capsule already IN FLIGHT
      // is the exception: it is mid-air, and snapping it would be the glitch.
      if (!state.morph && from !== 'island' && event.kind !== 'island') {
        return { occupant: event.kind, morph: null };
      }
      return { occupant: event.kind, morph: { from, to: event.kind } };
    }
    case 'settle':
      return state.morph ? { occupant: state.occupant, morph: null } : state;
    default:
      return state;
  }
}

/**
 * Who should own the slot. The slot is now the PILL's alone — the minimized call
 * island renders independently (see `VoiceCallIsland`) so the two can COEXIST: the
 * pill stays in the header while the call island floats. (This deliberately
 * reverses the earlier "island replaces the pill" morph, by request.) The
 * `islandVisible` field is accepted for call-site compatibility but ignored.
 */
export function desiredChip(input: { islandVisible?: boolean; pillEligible: boolean }): ChipKind | null {
  return input.pillEligible ? 'pill' : null;
}

/** The real chip that is mounted AND painted. Null while the overlay owns the slot. */
export function paintedChip(state: ChipSlotState): ChipKind | null {
  return state.morph ? null : state.occupant;
}

/**
 * The real chip that is in the DOM. During a morph the incoming chip is still
 * mounted — hidden, but laid out, so the overlay can measure the rect it has to
 * land on and so the slot's own box never collapses and jolts the header.
 */
export function mountedChip(state: ChipSlotState): ChipKind | null {
  return state.occupant;
}

/** What the user sees in the slot right now — the assertable invariant. */
export type SlotOccupancy = ChipKind | 'morph' | 'empty';

export function slotOccupancy(state: ChipSlotState): SlotOccupancy {
  if (state.morph) return 'morph';
  return state.occupant ?? 'empty';
}
